/**
 * Reading a supplier's prices back out of the RFQ workbook they returned — WITHOUT creating a
 * `Supplier Quotation`.
 *
 * ---- Why this exists ----
 *
 * Measured on the live bench, 2026-08-08. Supplier Shiby answered RFQ-2026-00011 and the reply is
 * correctly linked (`Communication 70pvs1vfma`, `reference_name: RFQ-2026-00011`). Its BODY,
 * however, contains nothing of theirs — only our own outgoing mail quoted back. Everything they
 * offered is in the returned workbook: `UNIT PRICE 23`, `AMOUNT 690`, `CURRENCY AED`, against
 * `UOM BAG`. So a screen that reads only the mail body shows a purchaser an empty reply, and the
 * Supplier Quotations table showed dashes and "Awaiting" while a priced answer sat one click away.
 *
 * This module makes that offer VISIBLE. It deliberately does not make it official: turning a reply
 * into a `Supplier Quotation` stays an explicit act ("Read as quotation"), because
 * `parse_supplier_reply` run against a "Sorry no stock" mail produced a quotation holding the
 * RFQ's line at rate 0.00 and reported `matched: 1` — a refusal recorded as an offer of free
 * goods, which would then win the comparison. See `useRfqReplies.ts`'s header and ask `E12`.
 *
 * ---- Why columns are found by LABEL, not by letter ----
 *
 * The workbook is generated SERVER-side. Addressing `F19` for the unit price would keep working
 * right up until someone inserts a column, and would then read the wrong number silently, which is
 * the worst possible failure for a price. Matching the header row's labels fails loudly instead:
 * an unrecognised layout returns `null` and the UI says it could not read the workbook.
 *
 * The real header row, measured from the file Shiby returned:
 *
 * ```
 * SR NO | IMPA CODE | ITEM DESCRIPTION | QTY | UOM | UNIT PRICE | AMOUNT |
 * DELIVERY TIME (Days) | SUPPLIER REMARKS | Opportunity Item
 * ```
 */

import type { XlsxCell, XlsxSheetTable } from './xlsxPreview'

/** One priced line as the supplier returned it. */
export interface OfferedLine {
  /** The supplier's serial number for the row, echoed from what we sent. */
  sno: string
  item_code: string
  description: string
  qty: number | null
  uom: string
  unit_price: number | null
  amount: number | null
  /** Free text: the template asks for a number of days, but suppliers write "2 weeks". Never
   *  coerced to a number — see `describeParseFailure`'s `B4`, where exactly that coercion put a
   *  day count into a Date column and failed the whole parse. */
  delivery: string
  remark: string
  /** The `Opportunity Item` through-key, so a line can be matched back to the enquiry row it
   *  came from rather than by description. String, never coerced. */
  opportunity_item: string
}

export interface WorkbookOffer {
  /** From the sheet's `RFQ ID:` header cell — the caller MUST check it against the RFQ on screen. */
  rfq_id: string | null
  /** From `CURRENCY:`. A price with no currency is not a price, so the UI shows it as unknown. */
  currency: string | null
  lines: OfferedLine[]
  /** How many lines the supplier actually put a price against. Zero means they returned the
   *  workbook untouched — which is a real thing suppliers do, and reads very differently from
   *  "they quoted". */
  priced_count: number
}

/** Header labels this reader needs to recognise, normalised. */
const COLUMN_KEYS = {
  sno: ['SR NO', 'SL NO', 'S NO'],
  item_code: ['IMPA CODE', 'ITEM CODE'],
  description: ['ITEM DESCRIPTION', 'DESCRIPTION'],
  qty: ['QTY', 'QUANTITY'],
  uom: ['UOM'],
  unit_price: ['UNIT PRICE'],
  amount: ['AMOUNT'],
  delivery: ['DELIVERY TIME'],
  remark: ['SUPPLIER REMARKS', 'REMARKS'],
  opportunity_item: ['OPPORTUNITY ITEM'],
} as const

type ColumnKey = keyof typeof COLUMN_KEYS

const normalise = (s: string): string => s.trim().toUpperCase().replace(/\s+/g, ' ')

/**
 * Column index per known field, or `-1`.
 *
 * Prefix matching, so `DELIVERY TIME (Days)` matches `DELIVERY TIME` — the parenthetical is a
 * hint to the supplier, not part of the field's identity, and has already changed once.
 */
export function locateColumns(columns: readonly string[]): Record<ColumnKey, number> {
  const found = {} as Record<ColumnKey, number>
  const headers = columns.map(normalise)
  for (const key of Object.keys(COLUMN_KEYS) as ColumnKey[]) {
    found[key] = headers.findIndex((h) => COLUMN_KEYS[key].some((label) => h === label || h.startsWith(`${label} `)))
  }
  return found
}

/**
 * Value following a `LABEL:` cell in the flattened header block.
 *
 * `buildSheetPreview` joins each header row's non-empty cells with three spaces, so a row reads
 * `CURRENCY:   AED   DELIVERY NEEDED BY:`. A label whose own value is BLANK is therefore followed
 * immediately by the next label — real on this bench, where `SUPPLIER REF NO:` is empty and the
 * line reads `SUPPLIER REF NO:   ENQUIRY NO:   ENQ-2026-00010`. Returning `ENQUIRY NO:` there
 * would be a fabricated value, so anything ending in `:` is rejected as the answer.
 */
export function headerValue(headerLines: readonly string[], label: string): string | null {
  const wanted = normalise(label)
  for (const line of headerLines) {
    const parts = line.split(/\s{3,}/).map((p) => p.trim()).filter((p) => p !== '')
    const at = parts.findIndex((p) => normalise(p) === `${wanted}:` || normalise(p) === wanted)
    if (at === -1) continue
    const next = parts[at + 1]
    if (next === undefined || next.endsWith(':')) return null
    return next
  }
  return null
}

const text = (row: readonly XlsxCell[], index: number): string => (index < 0 ? '' : (row[index]?.text ?? '').trim())

/** `null` for blank or unparseable — never `0`, which would read as "quoted at nil". */
function numberAt(row: readonly XlsxCell[], index: number): number | null {
  const raw = text(row, index)
  if (raw === '') return null
  // Suppliers type "1,250.00" and "AED 23". Strip anything that is not part of a number, but only
  // AFTER establishing there was something there — so a blank stays null rather than becoming 0.
  const cleaned = raw.replace(/[^0-9.\-]/g, '')
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return null
  const n = Number(cleaned)
  return Number.isFinite(n) ? n : null
}

/**
 * Extract the offer, or `null` when this sheet is not a recognisable RFQ workbook.
 *
 * `null` is the honest answer for an unreadable layout: the UI says so and falls back to the
 * "Read as quotation" path, rather than showing a number it guessed the meaning of.
 */
export function readWorkbookOffer(sheet: XlsxSheetTable): WorkbookOffer | null {
  const columns = locateColumns(sheet.columns)
  // A price column and something to identify the line by. Without both, this is some other
  // spreadsheet the supplier attached — a catalogue, a scan — and not our template.
  if (columns.unit_price < 0 || (columns.item_code < 0 && columns.description < 0)) return null

  const lines = sheet.rows
    .map((row): OfferedLine => ({
      sno: text(row, columns.sno),
      item_code: text(row, columns.item_code),
      description: text(row, columns.description),
      qty: numberAt(row, columns.qty),
      uom: text(row, columns.uom),
      unit_price: numberAt(row, columns.unit_price),
      amount: numberAt(row, columns.amount),
      delivery: text(row, columns.delivery),
      remark: text(row, columns.remark),
      opportunity_item: text(row, columns.opportunity_item),
    }))
    // A trailing blank row, or a totals row with no item, is not a line the supplier quoted.
    .filter((line) => line.item_code !== '' || line.description !== '')

  return {
    rfq_id: headerValue(sheet.headerLines, 'RFQ ID'),
    currency: headerValue(sheet.headerLines, 'CURRENCY'),
    lines,
    priced_count: lines.filter((l) => l.unit_price !== null).length,
  }
}

/**
 * The sheet holding the RFQ table, from a parsed workbook.
 *
 * The generated file carries a hidden `CurrencyList` tab of 148 ISO codes alongside the `RFQ`
 * one, so "the first sheet" is not a safe rule — it is only right by luck of ordering today.
 */
export function pickRfqSheet(sheets: readonly XlsxSheetTable[]): XlsxSheetTable | null {
  const named = sheets.find((s) => normalise(s.name) === 'RFQ')
  if (named && locateColumns(named.columns).unit_price >= 0) return named
  return sheets.find((s) => locateColumns(s.columns).unit_price >= 0) ?? null
}
